<template>
  <div class="space-y-6">
    <!-- Back Button -->
    <button
      @click="$emit('back')"
      class="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
    >
      <svg
        class="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 19l-7-7 7-7"
        />
      </svg>
      <span class="text-xl font-medium">Back</span>
    </button>

    <!-- Settings Card -->
    <div class="bg-white rounded-xl shadow-md p-6">
      <!-- Header -->
      <div class="border-b border-gray-200 pb-4 mb-6">
        <h2 class="text-2xl font-semibold text-gray-900">Settings</h2>
        <p class="text-base font-medium text-gray-600">
          Manage your kiosk settings.
        </p>
      </div>

      <!-- Settings List -->
      <div class="space-y-6">
        <!-- Require USAW Membership -->
        <div v-if="showUsawSetting" class="flex items-center justify-between">
          <div class="flex flex-col gap-2 flex-1 pr-4">
            <span class="text-xl font-medium text-gray-900">
              Require USAW Membership
            </span>
            <span class="text-base font-medium leading-6 text-gray-600">
              Only athletes with a valid USA Wrestling membership can check in.
            </span>
          </div>
          <button
            @click="toggleSetting('requireUsaw')"
            class="relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2"
            :class="settings.requireUsaw ? 'bg-green-500' : 'bg-gray-300'"
            role="switch"
            :aria-checked="settings.requireUsaw"
          >
            <span
              class="pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
              :class="settings.requireUsaw ? 'translate-x-6' : 'translate-x-0.5'"
              style="margin-top: 2px;"
            />
          </button>
        </div>

        <!-- Require AAU Membership -->
        <div v-if="showAauSetting" class="flex items-center justify-between">
          <div class="flex flex-col gap-2 flex-1 pr-4">
            <span class="text-xl font-medium text-gray-900">
              Require AAU Membership
            </span>
            <span class="text-base font-medium leading-6 text-gray-600">
              Only athletes with a valid AAU membership can check in.
            </span>
          </div>
          <button
            @click="toggleSetting('requireAau')"
            class="relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2"
            :class="settings.requireAau ? 'bg-green-500' : 'bg-gray-300'"
            role="switch"
            :aria-checked="settings.requireAau"
          >
            <span
              class="pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
              :class="settings.requireAau ? 'translate-x-6' : 'translate-x-0.5'"
              style="margin-top: 2px;"
            />
          </button>
        </div>

        <!-- Allow General Check-in -->
        <div class="flex items-center justify-between">
          <div class="flex flex-col gap-2 flex-1 pr-4">
            <span class="text-xl font-medium text-gray-900">
              Allow General Check-in
            </span>
            <span class="text-base font-medium leading-6 text-gray-600">
              Allow check-ins not tied to an event on the calendar.
            </span>
          </div>
          <button
            @click="toggleSetting('allowGeneralCheckIn')"
            class="relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2"
            :class="settings.allowGeneralCheckIn ? 'bg-green-500' : 'bg-gray-300'"
            role="switch"
            :aria-checked="settings.allowGeneralCheckIn"
          >
            <span
              class="pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
              :class="settings.allowGeneralCheckIn ? 'translate-x-6' : 'translate-x-0.5'"
              style="margin-top: 2px;"
            />
          </button>
        </div>

        <!-- Warp Speed -->
        <div class="flex items-center justify-between">
          <div class="flex flex-col gap-2 flex-1 pr-4">
            <span class="text-xl font-medium text-gray-900">
              Warp Speed
            </span>
            <span class="text-base font-medium leading-6 text-gray-600">
              Automatically check in wrestlers when there's only one eligible practice. Skips the confirmation screen.
            </span>
          </div>
          <button
            @click="toggleSetting('warpSpeed')"
            class="relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2"
            :class="settings.warpSpeed ? 'bg-green-500' : 'bg-gray-300'"
            role="switch"
            :aria-checked="settings.warpSpeed"
          >
            <span
              class="pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
              :class="settings.warpSpeed ? 'translate-x-6' : 'translate-x-0.5'"
              style="margin-top: 2px;"
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "KioskSettings",
  props: {
    store: {
      type: Object,
      required: true,
    },
  },
  emits: ["back", "settings-changed"],
  data() {
    return {
      settings: {
        requireUsaw: true,
        requireAau: true,
        allowGeneralCheckIn: false,
        warpSpeed: false,
      },
    };
  },
  computed: {
    teamInfo() {
      return this.store?.state.teamInfo || {};
    },
    showUsawSetting() {
      // Only show USAW setting if team has USAW memberships enabled
      return (
        this.teamInfo.usaw_memberships &&
        this.teamInfo.usaw_memberships !== "usaw_disabled"
      );
    },
    showAauSetting() {
      // Only show AAU setting if team has AAU memberships enabled
      return (
        this.teamInfo.aau_memberships &&
        this.teamInfo.aau_memberships !== "aau_disabled"
      );
    },
  },
  mounted() {
    this.loadSettings();
  },
  methods: {
    loadSettings() {
      // Load settings from localStorage
      try {
        const savedSettings = JSON.parse(
          localStorage.getItem(this.store.settingsKey())
        );
        if (savedSettings) {
          this.settings = {
            ...this.settings,
            ...savedSettings,
          };
        } else {
          // Default: enable membership requirements if team has them configured
          this.settings.requireUsaw = this.showUsawSetting;
          this.settings.requireAau = this.showAauSetting;
        }
      } catch (e) {
        console.error("Failed to load kiosk settings:", e);
      }
    },
    saveSettings() {
      try {
        localStorage.setItem(
          this.store.settingsKey(),
          JSON.stringify(this.settings)
        );
        this.$emit("settings-changed", this.settings);
      } catch (e) {
        console.error("Failed to save kiosk settings:", e);
      }
    },
    toggleSetting(key) {
      this.settings[key] = !this.settings[key];
      this.saveSettings();
    },
  },
};
</script>
